import React, { useEffect, useRef, useState } from 'react';
import cx from 'classnames';
import { useTransition, animated } from 'react-spring';
import { caret } from '../icon';
import { useAuth0 } from '@auth0/auth0-react';
import { MENUS_TO_REMOVE } from '../../config';
interface Link {
  readonly title?: string;
  readonly link?: string;
  readonly newTab?: boolean;
  children?: Link[];
  parent?: Link;
}

interface MenuProps {
  link: Link;
  onClick: (link: Link) => void;
  isAuthenticated: boolean;
}

const normalizePathname = (value: string, baseOrigin: string) => {
  try {
    const parsed = new URL(value, baseOrigin);
    let pathname = parsed.pathname;
    if (pathname.length > 1 && pathname.endsWith('/')) {
      pathname = pathname.slice(0, -1);
    }
    return { origin: parsed.origin, pathname };
  } catch {
    return { origin: '', pathname: '' };
  }
};

const Menu = ({ link, onClick, isAuthenticated }: MenuProps) => {
  const currentOrigin = window.location.origin;
  const currentPathname = normalizePathname(window.location.href, currentOrigin).pathname;
  const isActiveLink = (value?: string) => {
    if (!value || value.startsWith('#')) {
      return false;
    }
    const { origin, pathname } = normalizePathname(value, currentOrigin);
    return origin === currentOrigin && pathname === currentPathname;
  };

  // Exclude these links when user is authenticated

  return (
    <div className="p-6">
      <div className="border-t border-white border-opacity-50">
        {/* Header title */}
        {link?.title && (
          <button
            type="button"
            key={link?.title}
            onClick={() => onClick(link)}
            className="flex items-center justify-between  w-full  text-blue-darker sky-h4 border-b border-gray-dark border-opacity-50 py-3 hover:font-bold hover:text-blue-light"
          >
            <div className="text-left h-8 leading-8">{link?.title}</div>
            {link?.parent && (
              <div
                className="w-8 h-8 absolute transform rotate-90 -translate-y-1/2"
                style={{ right: '100%', top: '50%' }}
              >
                {caret}
              </div>
            )}
          </button>
        )}

        {/* Child links */}
        {link?.children
          ?.filter(child => {
            const name = child?.title?.trim().toLowerCase() || '';
            return !(isAuthenticated && MENUS_TO_REMOVE.includes(name));
          })
          .map(childLink => {
            const isActive = isActiveLink(childLink?.link);
            return (
              <button
              type="button"
              key={childLink?.title}
              onClick={() => onClick(childLink)}
              className={cx(
                'flex items-center justify-between w-full text-blue-darker sky-h4 border-b border-gray-dark border-opacity-50 py-3 hover:font-bold hover:text-blue-light',
                {
                  'underline font-bold font-skyBold': isActive,
                }
                )}
              >
                <div className="text-left h-8 leading-8">{childLink?.title}</div>
                {Array.isArray(childLink?.children) && childLink.children.length > 0 ? (
                  <div className="w-8 h-8 transform -rotate-90">{caret}</div>
                ) : null}
              </button>
            );
          })}
      </div>
    </div>
  );
};

let setParent = (link: Link, parent?: Link): Link => {
  let newLink = { ...link };
  if (newLink?.children) {
    newLink.children = newLink.children.map(l => setParent(l, newLink));
  }
  newLink.parent = parent;
  return newLink;
};

interface SideNavProps {
  link: Link;
  close: () => void;
}

const SideNav = ({ link, close }: SideNavProps) => {
  const { isAuthenticated } = useAuth0();
  const [currentLink, set] = useState(() => setParent(link));
  const [forward, setDirection] = useState(true);
  const menuRef = useRef<HTMLDivElement | null>(null);

  const click = (link: Link) => {
    if (link === currentLink && link?.parent) {
      set(link.parent);
      setDirection(false);
    } else if (Array.isArray(link?.children) && link.children.length > 0) {
      set(link);
      setDirection(true);
    } else if (link?.link) {
      window.location.href = link.link;
      close();
    }
  };

  useEffect(() => {
    if (menuRef.current) {
      menuRef.current.scrollTop = 0;
    }
  }, [currentLink]);

  const transitions = useTransition<Link, { opacity: number; x: number }>(
    currentLink,
    item => item?.title ?? '',
    {
      opacity: 1,
      x: 0,
      initial: { opacity: 1, x: 0 },
      from: { opacity: 0, x: forward ? 100 : -100 },
      enter: { opacity: 1, x: 0 },
      leave: { opacity: 0, x: forward ? -100 : 100 },
      config: { tension: 320, friction: 28, clamp: true },
    }
  );

  return (
     <div ref={menuRef} className="menus relative flex-1 min-h-0 overflow-y-auto">
      {transitions.map(({ item, props, key }) => (
        <animated.div
          key={key}
          style={{
            opacity: props.opacity,
            transform: props.x.interpolate((value: number) => `translate3d(${value}%, 0, 0)`),
          }}
          className="absolute inset-0"
        >
          <Menu link={item} onClick={click} isAuthenticated={isAuthenticated} />
        </animated.div>
      ))}
    </div>
  );
};

export { SideNav };
